import { useState, useCallback, useEffect, useRef } from "react";
import {
  DirectBillingPayerGroupDto,
  DirectBillingSingleRxResponseDto,
  ValidityDto,
} from "../../../../../../api-client";
import { useLoadGkvBills } from "../../../../../../stores/Transactions/insurance-query";
import { useApi } from "../../../../../../api/ApiProvider";
import { ValidationMap, ValidationProgress, UseGkvBillsReturn } from "../types/interface";
import { MAX_PARALLEL_VALIDATIONS, VALIDATION_CHUNK_SIZE } from "../params/constants";
/**
 * Core hook for the GKV New Bill workflow.
 *
 * Orchestrates:
 * 1. Loading bills from the server (grouped by payer)
 * 2. Progressive chunked validation (parallel batches of 10)
 * 3. Local mutations (add/remove Rx) without refetching
 *
 * Validation is kept as a separate map, not embedded in Rx objects,
 * keeping server data immutable and enabling independent loading.
 */
export function useGkvBills(accountId: string): UseGkvBillsReturn {
  const [bills, setBills] = useState<DirectBillingPayerGroupDto[]>([]);
  const [localValidation, setLocalValidation] = useState<ValidationMap>({});
  const [isValidating, setIsValidating] = useState(false);
  const [validationProgress, setValidationProgress] = useState<ValidationProgress>({ completed: 0, total: 0 });

  const { transaction } = useApi();

  // ── Server queries ──
  const { data: serverBills, isLoading, refetch } = useLoadGkvBills(accountId);

  // Tracks whether we've already kicked off validation for the initial load
  const hasInitializedRef = useRef(false);
  // Allows aborting in-flight validation requests on reload/unmount
  const abortControllerRef = useRef<AbortController | null>(null);

  // ── Chunked parallel validation ──

  /**
   * Validates Rx IDs in parallel chunks.
   * Merges results progressively into localValidation as each chunk completes.
   */
  const validateInChunks = useCallback(
    async (rxIds: string[]) => {
      if (rxIds.length === 0) return;

      // Abort any previous in-flight validation
      abortControllerRef.current?.abort();
      const controller = new AbortController();
      abortControllerRef.current = controller;

      setIsValidating(true);
      setValidationProgress({ completed: 0, total: rxIds.length });

      /**
       * Split requests into smaller chunks instead of sending all rxIds at once.
       *
       * This allows validation results to be processed and displayed progressively,
       * so users can see updates arriving rx by rx without waiting for the entire
       * request to complete.
       */
      const chunks: string[][] = [];
      for (let i = 0; i < rxIds.length; i += VALIDATION_CHUNK_SIZE) {
        chunks.push(rxIds.slice(i, i + VALIDATION_CHUNK_SIZE));
      }

      /**
       * Concurrency pool: index-based cursor shared across workers. A common pattern used for
       * worker queue.
       */
      let index = 0;
      const runWorker = async () => {
        while (true) {
          const currentIndex = index++;

          /**
           * Loop Breaking Conditions. Basically stop sending request.
           */
          if (currentIndex >= chunks.length) break;
          if (controller.signal.aborted) return;

          /**
           * Points the cursor on the correct chunk
           */
          const chunk = chunks[currentIndex];
          try {
            const result = await transaction.validateGkvBillRxs(
              { rxIds: chunk },
              { signal: controller.signal },
            );
            /**
             * Update the local state to render on the UI.
             */
            if (!controller.signal.aborted) {
              setLocalValidation((prev) => ({
                ...prev,
                ...(result as unknown as ValidationMap),
              }));
              setValidationProgress((prev) => ({
                ...prev,
                completed: prev.completed + chunk.length,
              }));
            }
          } catch (error: any) {
            if (error?.name === "CanceledError" || controller.signal.aborted) return;
            console.error("Validation chunk failed:", error);
          }
        }
      };

      /**
       * Fire up to MAX_PARALLEL workers.
       */
      const workerCount = Math.min(MAX_PARALLEL_VALIDATIONS, chunks.length);
      const workers = Array.from({ length: workerCount }, () => runWorker());
      await Promise.all(workers);

      if (!controller.signal.aborted) {
        setIsValidating(false);
      }
    },
    [transaction],
  );

  // ── Sync server data → local state ──

  /** Initialize local bills when server data arrives, kick off validation once */
  useEffect(() => {
    if (serverBills) {
      setBills(serverBills);

      if (!hasInitializedRef.current) {
        hasInitializedRef.current = true;
        const allIds = serverBills.flatMap((b) => b.rxs.map((rx) => rx.id));
        validateInChunks(allIds);
      }
    }
  }, [serverBills, validateInChunks]);

  /** Cleanup: abort in-flight validations on unmount */
  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort();
    };
  }, []);

  // ── Local mutations ──

  /**
   * Adds a single Rx (from SpotlightSearch) into the local bill state.
   *
   * - If a payer group with the same `payerIK` exists, appends the Rx and recalculates `totalPrice`.
   * - Otherwise, creates a new payer group.
   * - Merges the Rx-level validation into `localValidation` so the indicator renders immediately.
   */
  const addRx = useCallback(
    (response: DirectBillingSingleRxResponseDto): boolean => {
      const { rx, payerIK, payerName, digitalReceivingCenter, paperReceivingCenter, validation } =
        response;

      // Duplicate check — scan all payer groups for this Rx ID
      const alreadyExists = bills.some((group) => group.rxs.some((r) => r.id === rx.id));
      if (alreadyExists) {
        return false;
      }

      setBills((prev) => {
        const existingIndex = prev.findIndex((b) => b.payerIK === payerIK);

        if (existingIndex !== -1) {
          // Add to existing payer group
          const updated = [...prev];
          const group = { ...updated[existingIndex] };
          group.rxs = [...group.rxs, rx];
          group.totalPrice = group.rxs.reduce((sum, r) => sum + r.price.netValue, 0);
          updated[existingIndex] = group;
          return updated;
        }

        // Create new payer group
        return [
          ...prev,
          {
            payerIK,
            payerName,
            digitalReceivingCenter: digitalReceivingCenter ?? null,
            paperReceivingCenter: paperReceivingCenter ?? null,
            rxs: [rx],
            totalPrice: rx.price.netValue,
          },
        ];
      });

      // Merge the single-Rx validation into the local map
      if (validation && Object.keys(validation).length > 0) {
        setLocalValidation((prev) => ({
          ...prev,
          [rx.id]: validation as Record<string, ValidityDto>,
        }));
      }
      return true;
    },
    [bills],
  );

  /**
   * Removes a single Rx from its payer group.
   *
   * Recalculates `totalPrice`, drops the group if empty, and cleans up the validation entry.
   */
  const removeRx = useCallback((rxId: string) => {
    setBills((prev) =>
      prev
        .map((group) => {
          if (!group.rxs.some((rx) => rx.id === rxId)) return group;

          const updatedRxs = group.rxs.filter((rx) => rx.id !== rxId);
          return {
            ...group,
            rxs: updatedRxs,
            totalPrice: updatedRxs.reduce((sum, r) => sum + r.price.netValue, 0),
          };
        })
        .filter((group) => group.rxs.length > 0),
    );

    setLocalValidation((prev) => {
      const next = { ...prev };
      delete next[rxId];
      return next;
    });
  }, []);

  /**
   * Removes an entire payer group and all its Rxs.
   *
   * Also cleans up all associated validation entries.
   */
  const removeAllByPayer = useCallback((payerIK: string) => {
    setBills((prev) => {
      const group = prev.find((b) => b.payerIK === payerIK);
      if (group) {
        // Clean up validation entries for removed Rxs
        const rxIds = group.rxs.map((rx) => rx.id);
        setLocalValidation((prevVal) => {
          const next = { ...prevVal };
          rxIds.forEach((id) => delete next[id]);
          return next;
        });
      }
      return prev.filter((b) => b.payerIK !== payerIK);
    });
  }, []);

  /**
   * Keeps only Rxs where every validator returned `"Valid"`.
   *
   * Rxs without validation data are also removed. Empty groups are dropped.
   */
  const filterValid = useCallback(() => {
    setBills((prev) =>
      prev
        .map((group) => {
          const validRxs = group.rxs.filter((rx) => {
            const rxValidation = localValidation[rx.id];
            return rxValidation && Object.values(rxValidation).every((v) => v.validity === "Valid");
          });
          return {
            ...group,
            rxs: validRxs,
            totalPrice: validRxs.reduce((sum, r) => sum + r.price.netValue, 0),
          };
        })
        .filter((group) => group.rxs.length > 0),
    );
  }, [localValidation]);

  /** Reload bills from server and re-run validation from scratch */
  const reload = useCallback(async () => {
    abortControllerRef.current?.abort();

    const { data } = await refetch();
    if (data) {
      setLocalValidation({});
      setBills(data);
      hasInitializedRef.current = true;
      const allIds = data.flatMap((b) => b.rxs.map((rx) => rx.id));
      validateInChunks(allIds);
    }
  }, [refetch, validateInChunks]);

  return {
    bills,
    validationMap: localValidation,
    isLoading,
    isValidating,
    validationProgress,
    addRx,
    removeRx,
    removeAllByPayer,
    filterValid,
    refetch: reload,
  };
}
